import React from "react";
import {
  Check,
  Inbox,
  LockKeyhole,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
} from "lucide-react";

const getPermissionId = (permission) => {
  return String(
    permission?.pk_permission_id || permission?.permission_id || "",
  );
};

const getPermissionName = (permission) => {
  return (
    permission?.permission_name || permission?.permissionName || "Permission"
  );
};

const getRoleId = (role) => {
  return String(role?.pk_role_id || role?.role_id || role?.id || "");
};

const getRoleName = (role) => {
  const rawRole =
    role?.role ||
    role?.roleName ||
    role?.role_name ||
    "Unnamed Role";

  return String(rawRole).split("|")[0]?.trim() || "Unnamed Role";
};

const getRoleStatus = (role) => {
  const rawRole =
    role?.role ||
    role?.roleName ||
    role?.role_name ||
    "";

  const statusFromRole = String(rawRole).split("|")[1]?.trim().toUpperCase();

  const directStatus =
    role?.is_active ??
    role?.status ??
    role?.role_status ??
    role?.active;

  if (statusFromRole === "Y" || statusFromRole === "N") {
    return statusFromRole;
  }

  return (
    directStatus === "Y" ||
    directStatus === 1 ||
    directStatus === "1" ||
    directStatus === true
  )
    ? "Y"
    : "N";
};

const isRoleActive = (role) => {
  return getRoleStatus(role) === "Y";
};

const getAdminId = (admin) => {
  if (!admin || typeof admin !== "object") return "";

  const id =
    admin.pk_admin_id ??
    admin.admin_id ??
    admin.adminId ??
    admin.id ??
    admin.admin_loginid ??
    admin.userId;

  return id === null || id === undefined ? "" : String(id).trim();
};

const getAdminName = (admin) => {
  const firstName = admin?.first_name || admin?.firstName || "";
  const lastName = admin?.last_name || admin?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return admin?.admin_username || fullName || "Unnamed Admin";
};

const getAdminLoginId = (admin) => {
  return String(admin?.admin_loginid || admin?.userId || "");
};

const getRoleNameById = (roles = [], roleId = "") => {
  const role = roles.find((item) => String(getRoleId(item)) === String(roleId));
  return role ? getRoleName(role) : "No Role";
};

const getSidebarAccessId = (item) => {
  return String(item?.id || item?.pk_sidebar_id || "");
};

const getMenuId = (item) => {
  return String(item?.pk_menu_id || item?.menuId || "");
};

const getSubmenuId = (item) => {
  return String(item?.pk_submenu_id || item?.submenuId || item?.id || "");
};

const getMenuTitle = (item) => {
  return item?.menu_title || item?.menuTitle || "Untitled Menu";
};

const getSubmenuTitle = (item) => {
  return (
    item?.submenu_title || item?.submenuTitle || item?.menu_title || "Untitled"
  );
};

const getMenuStatus = (item) => {
  return (
    item?.status === "Y" || item?.is_active === "Y" || item?.is_active === 1
  );
};

const buildAccessKey = (item) => {
  return `${getMenuId(item)}_${getSubmenuId(item)}`;
};

const buildSidebarSelectionKey = (item) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  const menuId = getMenuId(item);
  const submenuId = getSubmenuId(item);
  const itemId = String(item?.id || "");
  const url = String(item?.submenu_url || item?.menu_url || "");

  return [
    menuId || "menu",
    submenuId || itemId || "submenu",
    url || "no-url",
  ].join("_");
};

const buildAccessManagementKey = (item) => {
  return `${item?.permissionId}_${item?.menuId}_${item?.submenuId}`;
};

const groupPermissionsByModule = (permissions = []) => {
  return permissions.reduce((grouped, permission) => {
    const moduleName = permission?.module_name || "Other Permissions";

    if (!grouped[moduleName]) {
      grouped[moduleName] = [];
    }

    grouped[moduleName].push(permission);
    return grouped;
  }, {});
};

const buildRolePermissionRowMap = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return safeRolePermissions.reduce((map, item) => {
    if (!item || typeof item !== "object") return map;

    const permissionId =
      item.permission_id === null || item.permission_id === undefined
        ? ""
        : String(item.permission_id);

    if (!permissionId) return map;

    map[permissionId] = item;
    return map;
  }, {});
};

const buildActivePermissionIdSet = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return new Set(
    safeRolePermissions
      .filter(
        (item) =>
          item &&
          typeof item === "object" &&
          (item.is_active === "Y" ||
            item.is_active === 1 ||
            item.is_active === "1"),
      )
      .map((item) => item?.permission_id)
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map(String),
  );
};

const flattenSidebarAccess = (accessList = []) => {
  const result = [];

  accessList.forEach((menu) => {
    const submenus = Array.isArray(menu?.submenus) ? menu.submenus : [];

    if (submenus.length === 0) {
      result.push({
        id: getSidebarAccessId(menu),
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: menu?.status,
        submenu_title: menu?.menu_title,
        submenu_icon: menu?.menu_icon,
        submenu_url: menu?.menu_url,
      });
      return;
    }

    submenus.forEach((submenu) => {
      result.push({
        id: submenu?.id || menu?.id,
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: submenu?.id || menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: submenu?.status || menu?.status,
        submenu_title: submenu?.submenu_title || menu?.menu_title,
        submenu_icon: submenu?.submenu_icon || menu?.menu_icon,
        submenu_url: submenu?.submenu_url || menu?.menu_url,
      });
    });
  });

  return result;
};

const buildUserAccessMap = (userAccessList = []) => {
  const flatUserAccess = flattenSidebarAccess(userAccessList);

  return flatUserAccess.reduce((map, item) => {
    const key = buildAccessKey(item);
    map[key] = item;
    return map;
  }, {});
};

const searchPermissions = (permissions = [], searchText = "") => {
  const safePermissions = Array.isArray(permissions) ? permissions : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safePermissions;

  return safePermissions.filter((permission) => {
    if (!permission || typeof permission !== "object") return false;

    return [
      permission?.permission_name,
      permission?.permissionName,
      permission?.module_name,
      permission?.pk_permission_id,
      permission?.permission_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const searchAdmins = (admins = [], searchText = "") => {
  const safeAdmins = Array.isArray(admins) ? admins : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeAdmins;

  return safeAdmins.filter((admin) => {
    if (!admin || typeof admin !== "object") {
      return false;
    }

    return [
      getAdminName(admin),
      getAdminLoginId(admin),
      getAdminId(admin),
      admin?.first_name,
      admin?.last_name,
      admin?.admin_username,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const normalizeSearchValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
};

const searchSidebarAccess = (accessList = [], searchText = "") => {
  const safeList = Array.isArray(accessList) ? accessList : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeList;

  return safeList.filter((item) => {
    if (!item || typeof item !== "object") return false;

    return [
      item?.menu_title,
      item?.submenu_title,
      item?.menu_url,
      item?.submenu_url,
      item?.id,
      item?.pk_menu_id,
      item?.pk_submenu_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const getModuleCount = (permissions = []) => {
  return Object.keys(groupPermissionsByModule(permissions)).length;
};

const InlineLoader = ({ text = "Loading...", className = "" }) => {
  return (
    <div
      className={`flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 ${className}`}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
};

const EmptyState = ({
  title = "No data found",
  description = "Nothing to show here.",
}) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <div className="mb-3 rounded-full bg-white p-3 shadow-sm">
        <Inbox className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <p className="mt-1 max-w-sm text-xs text-slate-500">{description}</p>
    </div>
  );
};

const PermissionGroupCard = ({
  moduleName,
  permissions = [],
  selectedPermissionIds = [],
  activePermissionIds,
  rolePermissionRowMap = {},
  mode = "create",
  disabled = false,
  onToggle,
}) => {
  const selectedSet = new Set(selectedPermissionIds.map(String));
  const isManageMode = mode === "manage";

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{moduleName}</h3>
          <p className="text-xs text-slate-500">
            {permissions.length} permissions
          </p>
        </div>

        <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500 shadow-sm">
          Module
        </div>
      </div>

      <div className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-3">
        {permissions.map((permission) => {
          const permissionId = getPermissionId(permission);
          const roleRow = rolePermissionRowMap[String(permissionId)];

          const checked = isManageMode
            ? activePermissionIds?.has(String(permissionId))
            : selectedSet.has(String(permissionId));

          const isMappingMissing =
            isManageMode && !roleRow?.pk_permission_roles_id;

          return (
            <label
              key={permissionId}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                checked
                  ? "border-orange-200 bg-orange-50"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              } ${
                disabled || isMappingMissing
                  ? "cursor-not-allowed opacity-70"
                  : ""
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled || isMappingMissing}
                onChange={(event) =>
                  onToggle?.(permission, event.target.checked)
                }
                className="sr-only"
              />

              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                  checked
                    ? "border-orange-500 bg-orange-500 text-white"
                    : "border-slate-300 bg-white text-transparent"
                }`}
              >
                <Check className="h-3.5 w-3.5" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-800">
                  {getPermissionName(permission)}
                </span>

                <span className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                  <LockKeyhole className="h-3 w-3" />
                  ID: {permissionId}
                  {isMappingMissing ? " • Mapping missing" : ""}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const RoleListPanel = ({
  roles = [],
  selectedRoleId = "",
  rolesLoading = false,
  updateLoading = false,
  onRefresh,
  onSelectRole,
  onUpdateRoleStatus,
}) => {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 p-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Roles</h2>
          <p className="text-xs text-slate-500">
            Select role to manage permissions
          </p>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={rolesLoading}
          className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw
            className={`h-4 w-4 ${rolesLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">
        {rolesLoading ? (
          <InlineLoader text="Loading roles..." />
        ) : roles.length === 0 ? (
          <EmptyState
            title="No roles found"
            description="Create a role first, then assign it to admin users."
          />
        ) : (
          <div className="space-y-2">
            {roles.map((role) => {
  const roleId = getRoleId(role);
  const isSelected = String(selectedRoleId) === String(roleId);
  const active = isRoleActive(role);

              return (
                <div
                  key={roleId}
                  className={`rounded-xl border p-3 transition ${
                    isSelected
                      ? "border-orange-200 bg-orange-50"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <span
  className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${
    active
      ? "bg-emerald-100 text-emerald-700"
      : "bg-rose-100 text-rose-700"
  }`}
>
  {active ? "Active" : "Inactive"}
</span>

                  <button
                    type="button"
                    onClick={() => onSelectRole(roleId)}
                    className="flex w-full items-center gap-3 text-left"
                  >
                    <span
                      className={`rounded-xl p-2 ${
                        isSelected
                          ? "bg-orange-500 text-white"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <ShieldCheck className="h-4 w-4" />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold text-slate-900">
                        {getRoleName(role)}
                      </span>

                      <span className="text-xs text-slate-400">
                        Role ID: {roleId}
                      </span>
                    </span>
                  </button>

                 <div className="mt-3 flex gap-2 pl-11">
  <button
    type="button"
    disabled={updateLoading || active}
    onClick={() => onUpdateRoleStatus(roleId, "Y")}
    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
      active
        ? "cursor-not-allowed bg-emerald-500 text-white"
        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
    } disabled:opacity-70`}
  >
    {active ? "Active" : "Set Active"}
  </button>

  <button
    type="button"
    disabled={updateLoading || !active}
    onClick={() => onUpdateRoleStatus(roleId, "N")}
    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
      !active
        ? "cursor-not-allowed bg-rose-500 text-white"
        : "bg-rose-50 text-rose-700 hover:bg-rose-100"
    } disabled:opacity-70`}
  >
    {!active ? "Inactive" : "Set Inactive"}
  </button>
</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const RolePermissionEditor = ({
  selectedRole,
  permissions = [],
  rolePermissions = [],
  searchText = "",
  onSearchChange,
  rolePermissionsLoading = false,
  updateLoading = false,
  onTogglePermission,
}) => {
  if (!selectedRole) {
    return (
      <EmptyState
        title="Select a role"
        description="Choose any role from the left panel to view and update its permissions."
      />
    );
  }

  const filteredPermissions = searchPermissions(permissions, searchText);
  const groupedPermissions = groupPermissionsByModule(filteredPermissions);
  const activePermissionIds = buildActivePermissionIdSet(rolePermissions);
  const rolePermissionRowMap = buildRolePermissionRowMap(rolePermissions);

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_auto_minmax(0,1fr)] gap-4 overflow-hidden">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Manage Permissions
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Toggle permission status for the selected role.
            </p>
          </div>

          <div className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">
            Active: {activePermissionIds.size}
          </div>
        </div>
      </div>

      <div className="relative w-full lg:max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

        <input
          type="text"
          value={searchText}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search permission or module..."
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
        />
      </div>

      <div className="min-h-0 overflow-y-auto overscroll-contain pr-1">
        {rolePermissionsLoading ? (
          <InlineLoader text="Loading selected role permissions..." />
        ) : Object.keys(groupedPermissions).length === 0 ? (
          <EmptyState
            title="No permissions found"
            description="Try changing the search text or refresh permission keys."
          />
        ) : (
          <div className="space-y-4 pb-4">
            {Object.entries(groupedPermissions).map(([moduleName, items]) => (
              <PermissionGroupCard
                key={moduleName}
                mode="manage"
                moduleName={moduleName}
                permissions={items}
                activePermissionIds={activePermissionIds}
                rolePermissionRowMap={rolePermissionRowMap}
                disabled={updateLoading}
                onToggle={(permission, checked) => {
                  const permissionId = getPermissionId(permission);

                  const row = rolePermissionRowMap[String(permissionId)];

                  onTogglePermission?.(permission, row, checked);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function RolesPermissionsTab(props) {
  const {
    roles = [],
    selectedRoleId = "",
    rolesLoading = false,
    updateLoading = false,
    onRefresh,
    onSelectRole,
    onUpdateRoleStatus,
    selectedRole,
    permissions = [],
    rolePermissions = [],
    searchText = "",
    onSearchChange,
    rolePermissionsLoading = false,
    onTogglePermission,
  } = props;

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 overflow-y-auto lg:grid-cols-[280px_minmax(0,1fr)] lg:overflow-hidden">
      <div className="min-h-[300px] lg:min-h-0">
        <RoleListPanel
          roles={roles}
          selectedRoleId={selectedRoleId}
          rolesLoading={rolesLoading}
          updateLoading={updateLoading}
          onRefresh={onRefresh}
          onSelectRole={onSelectRole}
          onUpdateRoleStatus={onUpdateRoleStatus}
        />
      </div>

      <div className="h-full min-h-0 min-w-0 overflow-hidden">
        {roles.length === 0 ? (
          <EmptyState
            title="Create your first role"
            description="No roles are available from the API."
          />
        ) : (
          <RolePermissionEditor
            selectedRole={selectedRole}
            permissions={permissions}
            rolePermissions={rolePermissions}
            searchText={searchText}
            onSearchChange={onSearchChange}
            rolePermissionsLoading={rolePermissionsLoading}
            updateLoading={updateLoading}
            onTogglePermission={onTogglePermission}
          />
        )}
      </div>
    </div>
  );
}
